import { lazy } from 'react'
import { Navigate, createBrowserRouter } from 'react-router-dom'
import {
  AdminRouteLayout,
  PublicRouteLayout,
  UserRouteLayout
} from './components/layouts/RouteLayouts'

const Home = lazy(() => import('./pages/Home'))
const Course = lazy(() => import('./pages/Course'))
const CourseList = lazy(() => import('./pages/CourseList'))
const Lesson = lazy(() => import('./pages/Lesson'))
const Forum = lazy(() => import('./pages/Forum'))
const ForumDetail = lazy(() => import('./pages/ForumDetail'))
const ForumNew = lazy(() => import('./pages/ForumNew'))
const ForumEdit = lazy(() => import('./pages/ForumEdit'))
const Login = lazy(() => import('./pages/Login'))
const MyPage = lazy(() => import('./pages/Mypage'))
const Live = lazy(() => import('./pages/Live'))
const LiveDetail = lazy(() => import('./pages/LiveDetail'))
const LiveInvite = lazy(() => import('./pages/LiveInvite'))
const QuizList = lazy(() => import('./pages/QuizList'))
const QuizDetail = lazy(() => import('./pages/QuizDetail'))
const TestPayment = lazy(() => import('./pages/TestPayment'))
const AudioCourseList = lazy(() => import('./pages/AudioCourseList'))
const AudioCourse = lazy(() => import('./pages/AudioCourse'))
const ProjectLiveCourseList = lazy(() => import('./pages/ProjectLiveCourseList'))
const OfflineCourseList = lazy(() => import('./pages/OfflineCourseList'))
const Payment = lazy(() => import('./pages/Payment'))
const TradingSim = lazy(() => import('./pages/TradingSim'))
const TheorySpecialList = lazy(() => import('./pages/TheorySpecialList'))
const TheorySpecial = lazy(() => import('./pages/TheorySpecial'))
const JeonbanJongji = lazy(() => import('./pages/GanjangJongji'))
const HubanJongji = lazy(() => import('./pages/HubanJongji'))
const NotFound = lazy(() => import('./pages/NotFound'))

const Dashboard = lazy(() => import('./pages/admin/Dashboard'))
const AdminUser = lazy(() => import('./pages/admin/User'))
const AdminGroup = lazy(() => import('./pages/admin/Group'))
const AdminGroupUsers = lazy(() => import('./pages/admin/GroupUsersPage'))
const AdminApply = lazy(() => import('./pages/admin/Apply'))
const AdminProgress = lazy(() => import('./pages/admin/Progress'))
const AdminLessonLearning = lazy(() => import('./pages/admin/LessonLearning'))
const AdminCourse = lazy(() => import('./pages/admin/Course'))
const AdminLive = lazy(() => import('./pages/admin/Live'))
const AdminJeonbanJongji = lazy(() => import('./pages/admin/GanjangJongji'))
const AdminComment = lazy(() => import('./pages/admin/Comment'))
const AdminCommunity = lazy(() => import('./pages/admin/Community'))
const AdminPopupNotice = lazy(() => import('./pages/admin/PopupNotice'))
const AdminLoginHistory = lazy(() => import('./pages/admin/LoginHistory'))
const AdminWatchTimeRanking = lazy(() => import('./pages/admin/WatchTimeRanking'))
const AdminQuiz = lazy(() => import('./pages/admin/Quiz'))
const AdminPayment = lazy(() => import('./pages/admin/Payment'))
const AdminBilling = lazy(() => import('./pages/admin/Billing'))

const router = createBrowserRouter([
  {
    path: '/',
    element: <PublicRouteLayout />,
    children: [
      { index: true, element: <Home /> },
      { path: 'courses', element: <CourseList /> },
      { path: 'course/:courseId', element: <Course /> },
      { path: 'lesson/:lessonId', element: <Lesson /> },
      { path: 'audio-courses', element: <AudioCourseList /> },
      { path: 'audio-course/:courseId', element: <AudioCourse /> },
      { path: 'project-live-courses', element: <ProjectLiveCourseList /> },
      { path: 'offline-courses', element: <OfflineCourseList /> },
      { path: 'trading-sim', element: <TradingSim /> },
      { path: 'theory-special', element: <TheorySpecialList /> },
      { path: 'theory-special/:courseId', element: <TheorySpecial /> },
      { path: 'jeonban-jongji', element: <JeonbanJongji /> },
      { path: 'ganjang-jongji', element: <Navigate to="/jeonban-jongji" replace /> },
      { path: 'huban-jongji', element: <HubanJongji /> },
      { path: 'forum', element: <Forum /> },
      { path: 'forum/post/:id', element: <ForumDetail /> },
      { path: 'forum/:category', element: <Forum /> },
      { path: 'login', element: <Login /> },
      {
        element: <UserRouteLayout />,
        children: [
          { path: 'mypage', element: <MyPage /> },
          { path: 'payment', element: <Payment /> },
          { path: 'payment/invite/:token', element: <Payment /> },
          { path: 'forum/post/:id/edit', element: <ForumEdit /> },
          { path: 'forum/:category/new', element: <ForumNew /> }
        ]
      },
      { path: 'live', element: <Live /> },
      { path: 'live/:liveId', element: <LiveDetail /> },
      { path: 'live/invite/:token', element: <LiveInvite /> },
      { path: 'replay/*', element: <Navigate to="/project-live-courses" replace /> },
      { path: 'quiz', element: <QuizList /> },
      { path: 'quiz/:quizId', element: <QuizDetail /> },
      ...(process.env.NODE_ENV === 'development'
        ? [{ path: 'test/payment', element: <TestPayment /> }]
        : []),
      { path: '*', element: <NotFound /> }
    ]
  },
  {
    path: '/admin',
    element: <AdminRouteLayout />,
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      { path: 'dashboard', element: <Dashboard /> },
      { path: 'user', element: <AdminUser /> },
      { path: 'group', element: <AdminGroup /> },
      { path: 'group/:groupId/users', element: <AdminGroupUsers /> },
      { path: 'apply', element: <AdminApply /> },
      { path: 'progress', element: <AdminProgress /> },
      { path: 'lesson-learning/:lessonId', element: <AdminLessonLearning /> },
      { path: 'course', element: <AdminCourse /> },
      { path: 'replay', element: <Navigate to="/admin/course" replace /> },
      { path: 'live', element: <AdminLive /> },
      { path: 'jeonban-jongji', element: <AdminJeonbanJongji /> },
      { path: 'ganjang-jongji', element: <Navigate to="/admin/jeonban-jongji" replace /> },
      { path: 'comment', element: <AdminComment /> },
      { path: 'community', element: <AdminCommunity /> },
      { path: 'popup-notice', element: <AdminPopupNotice /> },
      { path: 'login-history', element: <AdminLoginHistory /> },
      { path: 'watch-time-ranking', element: <AdminWatchTimeRanking /> },
      { path: 'quiz', element: <AdminQuiz /> },
      { path: 'payment', element: <AdminPayment /> },
      { path: 'billing', element: <AdminBilling /> },
      { path: '*', element: <NotFound /> }
    ]
  }
])

export default router
